import { type ComponentConfig } from "@puckeditor/core";
import { ServiceAreas, slugifyAreaName, type ServiceAreasProps } from "./service-areas";
import { ServiceAreasFields } from "./service-areas.fields";
import { RegenerableTextField } from "../editor/RegenerableTextField";
import type { JobsiteBusinessContext } from "../../types/runtime-config";

function pathExists(knownPaths: string[], path: string): boolean {
  return knownPaths.some((p) => p === path || p === `${path}/`);
}

/**
 * The page this area should link to, chosen from the paths the site actually
 * has: the service×location combo under `baseUrl` when one was generated,
 * otherwise the plain location page. Returns undefined rather than guessing,
 * so a business with no location pages renders plain chips instead of 404s.
 */
function resolveAreaPagePath(
  slug: string,
  baseUrl: string | undefined,
  knownPaths: string[] | undefined,
): string | undefined {
  if (!slug) return undefined;

  const comboPath = baseUrl ? `${baseUrl.replace(/\/$/, "")}/${slug}` : undefined;
  const locationPath = `/locations/${slug}`;

  // The editor preview ships no page list; keep its existing baseUrl behaviour
  // rather than inventing a link the build would not produce.
  if (!knownPaths) return comboPath;

  if (comboPath && pathExists(knownPaths, comboPath)) return comboPath;
  if (pathExists(knownPaths, locationPath)) return locationPath;
  return undefined;
}

export const ServiceAreasConfig: ComponentConfig<ServiceAreasProps> = {
  label: "Service Areas",
  fields: {
    heading: {
      ...ServiceAreasFields.heading,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="serviceAreas.heading"
          label="Heading"
          multiline={false}
          fieldMeta={field?.metadata}
        />
      ),
    },
    subheading: {
      ...ServiceAreasFields.subheading,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="serviceAreas.subheading"
          label="Subheading"
          multiline={true}
          fieldMeta={field?.metadata}
        />
      ),
    },
    layout: ServiceAreasFields.layout,
    showLinks: ServiceAreasFields.showLinks,
    showDescriptions: ServiceAreasFields.showDescriptions,
    showMap: ServiceAreasFields.showMap,
    baseUrl: ServiceAreasFields.baseUrl,
    areas: ServiceAreasFields.areas as any,
    backgroundColor: ServiceAreasFields.backgroundColor,
  },
  /**
   * Live business service areas replace the hand-typed list entirely, so on any site that has
   * them the array below is edited and then thrown away. Compact never renders descriptions.
   */
  resolveFields: (data, { fields, metadata }) => {
    const visible = { ...(fields as Record<string, unknown>) };
    const businessAreas = (metadata as { business?: { serviceAreas?: unknown[] } } | undefined)
      ?.business?.serviceAreas;

    if (Array.isArray(businessAreas) && businessAreas.length > 0) {
      delete visible.areas;
    }

    if ((data.props as { layout?: string })?.layout === "compact") {
      delete visible.showDescriptions;
    }

    return visible as typeof fields;
  },
  defaultProps: {
    heading: "Areas We Serve",
    subheading:
      "Proudly serving homeowners throughout the greater metro area and surrounding communities.",
    layout: "grid",
    showLinks: true,
    showDescriptions: false,
    showMap: true,
    baseUrl: "/locations/",
    areas: [
      { name: "austin", displayName: "Austin, TX" },
      { name: "round-rock", displayName: "Round Rock, TX" },
      { name: "cedar-park", displayName: "Cedar Park, TX" },
      { name: "pflugerville", displayName: "Pflugerville, TX" },
      { name: "georgetown", displayName: "Georgetown, TX" },
      { name: "leander", displayName: "Leander, TX" },
    ],
  },
  render: ({ id, puck, editMode, ...props }) => {
    // Business data flows through puck.metadata.business (NEVER read window globals here)
    const metadata = puck?.metadata as
      | {
          business?: JobsiteBusinessContext;
          site?: { googleMapsApiKey?: string };
          pagePaths?: string[];
        }
      | undefined;
    const business = metadata?.business;
    const metaServiceAreas = business?.serviceAreas;
    const googleMapsApiKey = metadata?.site?.googleMapsApiKey;

    // Only use baseUrl if combo pages actually exist — prevents dead links on sites
    // that have service areas but no service_location pages generated yet.
    const knownPaths = metadata?.pagePaths;
    const resolvedBaseUrl =
      props.baseUrl && knownPaths
        ? knownPaths.some((p) => p.startsWith(props.baseUrl!))
          ? props.baseUrl
          : undefined
        : props.baseUrl;

    // Prefer metadata service areas over stored props (they reflect live business data)
    const resolvedAreas =
      metaServiceAreas && metaServiceAreas.length > 0
        ? metaServiceAreas.map((sa) => ({
            name: sa.name ?? "",
            slug: sa.slug,
            displayName: sa.displayName ?? sa.name ?? "",
            latitude: sa.latitude ?? null,
            longitude: sa.longitude ?? null,
            boundary: sa.boundary ?? null,
            surroundingAreas: sa.surroundingAreas ?? false,
            // The business payload carries no URL, so the area's own page is
            // matched out of the site's real paths. Without this the metadata
            // areas render unlinked and every location page is orphaned.
            url: resolveAreaPagePath(sa.slug ?? slugifyAreaName(sa.name ?? ""), props.baseUrl, knownPaths),
          }))
        : props.areas;

    return (
      <ServiceAreas
        id={id}
        {...props}
        baseUrl={resolvedBaseUrl}
        areas={resolvedAreas}
        googleMapsApiKey={googleMapsApiKey}
      />
    );
  },
};
